import {
    createLibraryFolder,
    getApiErrorMessage,
    IApiAxiosError,
    IClientMinimal,
    ILibraryFolderCreate,
} from "@/api";
import { ClientAutocomplete } from "@/components/shared/ClientAutocomplete";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface CreateFolderDialogProps {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
    parentId?: number | null;
    parentClient?: IClientMinimal | null;
    /** Inside a client workspace the client is implicit: hide the client
     *  select and force `client_id` to `parentClient`. The main library
     *  leaves this off and keeps the editable autocomplete. */
    lockClient?: boolean;
}

export const CreateFolderDialog = ({
    open,
    onClose,
    onCreated,
    parentId,
    parentClient,
    lockClient = false,
}: CreateFolderDialogProps) => {
    const queryClient = useQueryClient();
    const [name, setName] = useState("");
    const [selectedClient, setSelectedClient] = useState<IClientMinimal | null>(
        null
    );
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && parentClient) {
            setSelectedClient(parentClient);
        }
    }, [open, parentClient]);

    const { mutate, isPending } = useMutation({
        mutationFn: (data: ILibraryFolderCreate) => createLibraryFolder(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-folders"],
            });
            toast.success("Folder created successfully");
            handleClose();
            onCreated();
        },
        onError: (err: IApiAxiosError) => {
            setError(getApiErrorMessage(err, "Failed to create folder"));
        },
    });

    const handleClose = () => {
        setName("");
        setSelectedClient(null);
        setError(null);
        onClose();
    };

    const handleSubmit = () => {
        if (!name.trim()) {
            setError("Folder name is required");
            return;
        }
        setError(null);
        mutate({
            name: name.trim(),
            // Locked surfaces force the workspace client regardless of the
            // (hidden) autocomplete state; the main library uses the picker.
            client_id: lockClient
                ? (parentClient?.id ?? null)
                : (selectedClient?.id ?? null),
            parent_id: parentId ?? undefined,
        });
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Create Folder</DialogTitle>
                    <DialogDescription>
                        Create a new folder to organize your documents
                    </DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                    className="space-y-3"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="create-folder-name">Folder Name</Label>
                        <Input
                            id="create-folder-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isPending}
                            maxLength={100}
                        />
                    </div>

                    {!lockClient && (
                        <ClientAutocomplete
                            value={selectedClient}
                            onChange={setSelectedClient}
                            disabled={isPending}
                            label="Client (optional)"
                            helperText="Documents uploaded to this folder will inherit this client"
                        />
                    )}

                    {error && (
                        <p className="text-destructive text-sm">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isPending || !name.trim()}
                        >
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Create"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
